import React from 'react';
import { Button, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';

/**
 * Reusable Load More button for pagination
 *
 * @param {boolean} hasMore - Whether there are more items to load
 * @param {boolean} loading - Whether data is currently being loaded
 * @param {function} onLoadMore - Function to call when button is clicked
 * @param {number} dataLength - Current number of items loaded
 * @param {string} loadingText - Text to show while loading (default: "Loading...")
 * @param {string} loadMoreText - Text to show on button (default: "Load More")
 * @param {string} noMoreText - Text to show when no more data (default: "No more items to load")
 * @param {string} color - Button color (default: "primary")
 */
const LoadMoreButton = ({
  hasMore,
  loading,
  onLoadMore,
  dataLength = 0,
  loadingText = 'Loading...',
  loadMoreText = 'Load More',
  noMoreText = 'No more items to load',
  color = 'primary',
}) => {
  const { t } = useTranslation();
  
  const displayLoadingText = loadingText === 'Loading...' ? t('shared.loading', 'Loading...') : loadingText;
  const displayLoadMoreText = loadMoreText === 'Load More' ? t('shared.load', 'Load More') : loadMoreText;
  const displayNoMoreText = noMoreText === 'No more items to load' ? t('business.components.load_more_button.no_more_items', 'No more items to load') : noMoreText;

  if (dataLength === 0 && !loading) {
    return null;
  }

  return (
    <div className="text-center py-3">
      {loading ? (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner color={color} size="sm" className="me-2" />
          <span className="text-muted">{displayLoadingText}</span>
        </div>
      ) : hasMore ? (
        <Button
          color={color}
          outline
          onClick={onLoadMore}
          disabled={loading}
        >
          {displayLoadMoreText}
        </Button>
      ) : dataLength > 0 ? (
        <div className="text-muted small">{displayNoMoreText}</div>
      ) : null}
    </div>
  );
};

export default LoadMoreButton;
